import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { removeUser } from "../features/users/userSlice";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

function UserTable() {

    const dispatch = useDispatch();

    const { users = [] } = useSelector(
        (state) => state.users
    );

    const [filterText, setFilterText] = useState("");

    // =========================================================
    // DELETE USER
    // =========================================================

    const deleteUser = async (id) => {

        const result = await Swal.fire({
            title: "Delete User?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel"
        });

        if (!result.isConfirmed) return;

        try {

            await dispatch(removeUser(id)).unwrap();

            toast.success(
                "User deleted successfully."
            );

        } catch (error) {

            toast.error(
                error || "Delete failed."
            );

        }
    };


    // =========================================================
    // SEARCH
    // =========================================================

    const filteredItems = users.filter((item) => {

        const search =
            filterText.toLowerCase();

        return (

            (item.name || "")
                .toLowerCase()
                .includes(search)

            ||

            (item.email || "")
                .toLowerCase()
                .includes(search)

            ||

            (item.role || "")
                .toLowerCase()
                .includes(search)

        );

    });


    // =========================================================
    // PERMISSION COUNT
    // =========================================================

    const getPermissionCount = (user) => {

        if (user.role === "admin") {
            return "Full Access";
        }

        let count = 0;

        const permissions =
            user.permissions || {};

        Object.values(permissions).forEach(
            (modulePermissions) => {

                if (
                    modulePermissions &&
                    typeof modulePermissions === "object"
                ) {

                    Object.values(
                        modulePermissions
                    ).forEach((value) => {

                        if (value === true) {
                            count++;
                        }

                    });

                }

        });

        return `${count} permissions`;
    };


    // =========================================================
    // GET MODULE PERMISSION SUMMARY
    // =========================================================

    const getPermissionSummary = (user) => {

        if (user.role === "admin") {
            return "All Modules";
        }

        const permissions =
            user.permissions || {};

        const modules = [];

        Object.entries(permissions).forEach(
            ([moduleName, modulePermissions]) => {

                if (
                    modulePermissions &&
                    typeof modulePermissions === "object"
                ) {

                    const hasPermission =
                        Object.values(
                            modulePermissions
                        ).some(
                            value => value === true
                        );

                    if (hasPermission) {
                        modules.push(moduleName);
                    }
                }

            }
        );

        return modules.length
            ? modules.join(", ")
            : "No Permissions";
    };


    // =========================================================
    // TABLE COLUMNS
    // =========================================================

    const columns = [

        // -----------------------------------------------------
        // #
        // -----------------------------------------------------

        {
            name: "#",
            width: "60px",
            cell: (row, index) =>
                index + 1
        },


        // -----------------------------------------------------
        // USER
        // -----------------------------------------------------

        {
            name: "User",
            minWidth: "220px",
            selector: row => row.name,
            sortable: true,

            cell: (row) => (

                <div className="d-flex align-items-center">

                    <div
                        className={`rounded-circle d-flex align-items-center justify-content-center me-2 ${
                            row.role === "admin"
                                ? "bg-danger"
                                : "bg-primary"
                        }`}
                        style={{
                            width: "42px",
                            height: "42px"
                        }}
                    >

                        <i
                            className={
                                row.role === "admin"
                                    ? "bi bi-shield-lock text-white"
                                    : "bi bi-person text-white"
                            }
                        ></i>

                    </div>

                    <div>

                        <div className="fw-bold">
                            {row.name || "N/A"}
                        </div>

                        <small className="text-muted">
                            {row.email || "-"}
                        </small>

                    </div>

                </div>

            )
        },


        // -----------------------------------------------------
        // EMAIL
        // -----------------------------------------------------

        {
            name: "Email",
            selector: row => row.email,
            sortable: true
        },


        // -----------------------------------------------------
        // ROLE
        // -----------------------------------------------------

        {
            name: "Role",
            width: "120px",

            cell: (row) => (

                row.role === "admin" ? (

                    <span className="badge bg-danger">

                        <i className="bi bi-shield-fill-check me-1"></i>

                        Admin

                    </span>

                ) : (

                    <span className="badge bg-primary">

                        <i className="bi bi-person-fill me-1"></i>

                        User

                    </span>

                )

            )
        },


        // -----------------------------------------------------
        // PERMISSIONS
        // -----------------------------------------------------

        {
            name: "Permissions",
            minWidth: "180px",

            cell: (row) => (

                <div>

                    <div className="fw-semibold">

                        {getPermissionCount(row)}

                    </div>

                    <small
                        className="text-muted"
                        title={getPermissionSummary(row)}
                    >

                        {getPermissionSummary(row)}

                    </small>

                </div>

            )
        },


        // -----------------------------------------------------
        // STATUS
        // -----------------------------------------------------

        {
            name: "Status",
            width: "110px",

            cell: (row) => (

                row.isActive !== false ? (

                    <span className="badge bg-success">

                        <i className="bi bi-check-circle me-1"></i>

                        Active

                    </span>

                ) : (

                    <span className="badge bg-secondary">

                        <i className="bi bi-x-circle me-1"></i>

                        Inactive

                    </span>

                )

            )
        },


        // -----------------------------------------------------
        // CREATED
        // -----------------------------------------------------

        {
            name: "Created",
            width: "130px",
            selector: row => row.createdAt,
            sortable: true,

            cell: (row) => {

                if (!row.createdAt) {
                    return "-";
                }

                return new Date(
                    row.createdAt
                ).toLocaleDateString("en-IN");

            }

        },


        // -----------------------------------------------------
        // ACTION
        // -----------------------------------------------------

        {
            name: "Action",
            width: "130px",

            cell: (row) => (

                <div className="dropdown">

                    <button
                        className="btn btn-primary btn-sm dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                    >

                        <i className="bi bi-gear me-1"></i>

                        Actions

                    </button>

                    <ul className="dropdown-menu">

                        {/* EDIT */}

                        <li>

                            <Link
                                className="dropdown-item"
                                to={`/dashboard/edituser/${row._id}`}
                            >

                                <i className="bi bi-pencil-square me-2"></i>

                                Edit User

                            </Link>

                        </li>


                        {/* VIEW */}

                        <li>

                            <Link
                                className="dropdown-item"
                                to={`/dashboard/user/${row._id}`}
                            >

                                <i className="bi bi-eye me-2"></i>

                                View User

                            </Link>

                        </li>


                        {/* PERMISSIONS */}

                        <li>

                            <Link
                                className="dropdown-item"
                                to={`/dashboard/user/${row._id}/permissions`}
                            >

                                <i className="bi bi-key me-2"></i>

                                Permissions

                            </Link>

                        </li>


                        <li>

                            <hr className="dropdown-divider" />

                        </li>


                        {/* DELETE */}

                        <li>

                            <button
                                type="button"
                                className="dropdown-item text-danger"
                                onClick={() =>
                                    deleteUser(row._id)
                                }
                            >

                                <i className="bi bi-trash me-2"></i>

                                Delete User

                            </button>

                        </li>

                    </ul>

                </div>

            )
        }

    ];


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div className="card shadow border-0">

            {/* =================================================
                HEADER
            ================================================== */}

            <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">

                <h5 className="mb-0">

                    <i className="bi bi-people-fill me-2"></i>

                    User Management

                </h5>


                <span className="badge bg-light text-dark">

                    Total : {users.length}

                </span>

            </div>


            {/* =================================================
                BODY
            ================================================== */}

            <div className="card-body">

                {/* SEARCH */}

                <div className="row mb-3">

                    <div className="col-md-6">

                        <div className="input-group">

                            <span className="input-group-text">

                                <i className="bi bi-search"></i>

                            </span>

                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search name, email or role..."
                                value={filterText}
                                onChange={(e) =>
                                    setFilterText(
                                        e.target.value
                                    )
                                }
                            />

                        </div>

                    </div>


                    <div className="col-md-6 text-md-end mt-2 mt-md-0">

                        <Link
                            to="/dashboard/adduser"
                            className="btn btn-success"
                        >

                            <i className="bi bi-person-plus-fill me-2"></i>

                            Add User

                        </Link>

                    </div>

                </div>


                {/* TABLE */}

                <DataTable

                    columns={columns}

                    data={filteredItems}

                    pagination

                    paginationPerPage={10}

                    paginationRowsPerPageOptions={[
                        10,
                        20,
                        50,
                        100
                    ]}

                    highlightOnHover

                    striped

                    responsive

                    fixedHeader

                    fixedHeaderScrollHeight="500px"

                    persistTableHead

                    noDataComponent={
                        <div className="py-4 text-muted">

                            <i className="bi bi-people fs-3 d-block mb-2"></i>

                            No Users Found

                        </div>
                    }

                />

            </div>

        </div>

    );

}

export default UserTable;